import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Shipment } from '@shared-features/models/shipment';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { catchError, takeUntil } from 'rxjs/operators';
import { RequestLog } from '@shared-features/models/request-log.model';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { IntegrationsApiService } from '@features/shipments/services/integrations-api.service';
import { LayoutService } from '@shared/services/layout.service';
import { combineLatest, Observable, of } from 'rxjs';
import { ShipmentIntegration } from '@features/shipments/models/shipment-integration.model';
import { ShipmentIntegrationAction } from '@features/shipments/models/shipment-integration-action.model';
import { IntegrationActionStatus } from '@shared-features/enums/integration-action-status.enum';
import { IntegrationType } from '@features/shipments/enums/integration-type.enum';
import { ShipmentStatus } from '@shared-features/enums/shipment-status.enum';
import { UserRoleService } from '@core/services';
import { UserRole } from '@shared/enums/user-role.enum';
import { isAllowToCancelIntegration } from '@features/b2c-shipments/constants/shipment-b2c-validation-action';
import { B2cShipmentStatus } from '@features/b2c-shipments/enums/b2c-shipment-status.enum';
import { Truck } from '@features/fleets/models/truck';
import { TruckPlateNumberDetails } from '@features/fleets/models/plate-number-details.model';
import { ConfigConstant } from '@shared/constants/config.constant';

// Bayan's spec: "Driver's mobile number must start with +9665 and 8 digits"
const BAYAN_DRIVER_MOBILE_REGEX = /^\+9665\d{8}$/;

@Component({
  selector: 'app-integration-card',
  templateUrl: './integration-card.component.html',
  styleUrls: ['./integration-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegrationCardComponent extends BaseComponent implements OnInit {
  @Input() shipment: Shipment;
  @Input() isB2c: boolean;
  @Input() actionRoll: UserRole[];
  @Output() integrated = new EventEmitter<void>();

  integrationTabs: ShipmentIntegration[] = [];
  activeIntegrationTab = 0;
  layoutDirection$ = this.layoutService.websiteDirection$.asObservable() as Observable<any>;
  integrationActions: { [key: string]: ShipmentIntegrationAction } = {};

  haveAccess: boolean;

  actionPanels = {};
  disabledAction = {};

  cementLogs: RequestLog[];
  readonly ShipmentCreationTemplateTypes = ShipmentCreationTemplateTypes;
  readonly IntegrationActionStatus = IntegrationActionStatus;

  /** Bayan only accepts trips inside Saudi Arabia. An unknown country is not treated as outside. */
  get isBayanRouteOutsideSaudi(): boolean {
    return [this.shipment?.pickUpLocation, this.shipment?.dropOffLocation].some((location) => {
      const isoCode = location?.city?.country?.isoCode;
      return !!isoCode && isoCode !== ConfigConstant.SAUDI_ARABIA_ISO_CODE;
    });
  }

  get isBayanDisabled(): boolean {
    return this.shipment?.isBayanReady === false || this.isBayanRouteOutsideSaudi;
  }

  /**
   * Mirrors the backend Bayan readiness checks to explain why `isBayanReady` is false.
   * Order-oriented shipments skip the receiver/city/requests checks, as the backend validates
   * those per order leg instead.
   */
  get bayanMissingInfo(): string {
    if (!this.isBayanDisabled) {
      return '';
    }

    const {
      driver,
      truck,
      fleet,
      customer,
      receiver,
      dropOffCustomer,
      pickUpLocation,
      dropOffLocation,
      requests,
    } = this.shipment;
    const isOrderTemplate = this.shipment.shipmentCreationTemplate === ShipmentCreationTemplateTypes.Orders;

    const driverMobile = driver?.user?.mobileNumber;
    const isOutsideSaudi = this.isBayanRouteOutsideSaudi;

    const checks: [boolean, string][] = [
      [
        !isOrderTemplate &&
          !receiver?.contactPerson?.[0]?.mobileNumber &&
          !dropOffCustomer?.mobileNumber,
        'ReceiverMobileNumberNotExist',
      ],
      [
        !isOrderTemplate &&
          !isOutsideSaudi &&
          (!pickUpLocation?.city?.bayanCityId || !dropOffLocation?.city?.bayanCityId),
        'BayanCityNotExist',
      ],
      [!isOrderTemplate && !requests?.length, 'ShipmentHasNoItems'],
      [isOutsideSaudi, 'LocationOutsideSaudi'],
      [!driver?.id, 'DriverNotExist'],
      [!!driver?.id && !driver.iqamaId, 'InvalidIqamaId'],
      [!!driver?.id && !driver.issueNumber, 'DriverIssueNumberNotExist'],
      [!!driver?.id && !driverMobile, 'DriverMobileNumberNotExist'],
      [
        !!driverMobile && !BAYAN_DRIVER_MOBILE_REGEX.test(driverMobile.replace('-', '')),
        'DriverMobileNumberFormatInvalid',
      ],
      [!truck?.id, 'TruckNotExist'],
      [!!truck?.isExternal, 'TruckExternal'],
      [!!truck?.id && !this.isValidBayanPlate(truck), 'InvalidPlateNumber'],
      [!customer?.mobileNumber, 'CustomerMobileNumberNotExist'],
      [!fleet?.hasMoiNumber && !truck?.ownerId, 'FleetMoiNotExist'],
    ];

    const reasons = checks
      .filter(([isMissing]) => isMissing)
      .map(([, key]) => `• ${this.translate(`shipments.BayanMissingInfo.${key}`)}`);

    // The frontend checks can miss a backend rule (e.g. per order leg), so never show an empty tooltip.
    return reasons.length
      ? reasons.join('\n\n')
      : this.translate('shipments.BayanMissingInfo.Unknown');
  }

  /**
   * Mirrors the backend's `buildVehiclePlate`: Bayan needs 3 letters and 1-4 digits, read from
   * `plateNumberDetails` (JSON) when present, otherwise parsed out of `plateNumber`.
   */
  private isValidBayanPlate({ plateNumberDetails, plateNumber }: Truck): boolean {
    let letters: string[];
    let digits: string;

    try {
      if (plateNumberDetails) {
        const details: TruckPlateNumberDetails = JSON.parse(plateNumberDetails);
        letters = [details.leftCharAR, details.middleCharAR, details.rightCharAR];
        digits = `${details.plateNumberDigits}`;
      } else {
        const chars = plateNumber.split('').filter((char) => char !== ' ');
        letters = chars.filter((char) => isNaN(parseInt(char)));
        digits = chars.filter((char) => !isNaN(parseInt(char))).join('');
      }
    } catch {
      return false;
    }

    const [left, middle, right] = letters;
    return !!(left && middle && right) && /^\d{1,4}$/.test(digits);
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private integrationsApiService: IntegrationsApiService,
    private layoutService: LayoutService,
    private userRoleService: UserRoleService
  ) {
    super();
  }

  ngOnInit(): void {
    this.haveAccess = this.userRoleService.isUserHasRoles(this.actionRoll);
    this.getIntegrations();
    this.setDisabledActions();
  }
  private setDisabledActions(): void {
    this.disabledAction = {
      cancel:
        (this.isB2c && !this.haveAccess) ||
        (this.isB2c &&
          !isAllowToCancelIntegration(this.shipment.status as unknown as B2cShipmentStatus)),
      create: this.isB2c && !this.haveAccess,
    };
  }
  private getIntegrations(): void {
    this.load(this.getIntegrationsApi())
      .subscribe(integration => {
        this.buildIntegrationTabs(integration);
    });
  }

  private getIntegrationsApi(): Observable<ShipmentIntegration> {
    return this.isB2c
      ? this.integrationsApiService.getB2cShipmentIntegrations(this.shipment.id)
      : this.integrationsApiService.getShipmentIntegrations(this.shipment.id);
  }

  getCementLogs = (): void => {
    this.load(this.integrationsApiService.getCementOrderLogs(this.shipment?.id), {
      isLoadingTransparent: true,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((cementLogs) => {
        this.cementLogs = cementLogs;
        this.changeDetectorRef.markForCheck();
      });
  };

  integrateWithBayan = (): void => {
    this.load(
      this.integrationsApiService.integrateWithBayan(
        this.shipment.id,
        this.shipment.shipmentCreationTemplate
      ),
      {
        isLoadingTransparent: true,
      }
    ).subscribe(
      () => this.integrated.emit(),
      () => this.integrated.emit()
    );
  };

  private buildIntegrationTabs(integration: ShipmentIntegration) {
    const isArray = Array.isArray(integration);

    if (integration?.url) {
      integration.id = 1;
    }
    const isCementTemplate =
      (this.shipment as Shipment)?.shipmentCreationTemplate ===
      ShipmentCreationTemplateTypes.Cement;
    const bayanIntegration = Object.assign(new ShipmentIntegration(), {
      id: -1,
      url: '/assets/images/bayan.png',
    });
    const cementIntegration = Object.assign(new ShipmentIntegration(), {
      id: -2,
      url: '/assets/images/Cement_Logo.png',
    });

    this.integrationTabs = [
      !this.isB2c ? bayanIntegration : null,
      isCementTemplate && !this.isB2c ? cementIntegration : null,
    ]
      .concat(!isArray && integration ? [integration] : [])
      .filter(Boolean);
    this.handleActiveTabActions(this.activeIntegrationTab);
    this.changeDetectorRef.markForCheck();
  }

  cancelBayanWaybills(): void {
    this.cancelWaybill();
  }

  cancelOneBayanWayBill(wayBillId: number): void {
    this.cancelWaybill(wayBillId);
  }

  downloadBayanPDF = (): void => {
    if ((this.shipment as Shipment)?.isVerifiedByBayan) {
      this.load(this.integrationsApiService.downloadBayanPDF(this.shipment.id), {
        isLoadingTransparent: true,
      }).subscribe((url) => {
        window.open(url, '_blank');
      });
    }
  };

  integrateWithCement = (): void => {
    this.load(this.integrationsApiService.integrateWithCement(this.shipment.id), {
      isLoadingTransparent: true,
    }).subscribe((res) => this.integrated.emit());
  };

  handleActiveTabActions(index) {
    if (this.integrationTabs[index]?.id > -1) {
      this.integrationActions = this.integrationTabs[index]?.actionsWithLogs || {};
    } else {
      this.integrationActions = {};
    }
  }

  handleIntegrationTabChange(index: number): void {
    this.activeIntegrationTab = index;
    if (this.integrationTabs[index]?.id === -2) {
      this.getCementLogs();
    }
    this.handleActiveTabActions(index);
  }

  submitAction(actionName: string, integrationType: IntegrationType): void {
    this.load(
      !this.isB2c
        ? this.integrationsApiService.integrateShipment({
            shipmentId: this.shipment.id,
            type: actionName,
          })
        : this.integrationsApiService.fleetIntegrateShipment({
            shipmentId: this.shipment.id,
            type: actionName,
          })
    ).subscribe(
      (res) => {
        typeof res.data !== 'string'
          ? this.showSuccessMessage('SuccessMessages.IntegrationUpdatedSuccessfully')
          : this.showSuccessMessage('', res.data);

        this.getIntegrations();
        this.integrated.emit();
      },
      () => {
        this.getIntegrations();
        this.integrated.emit();
      }
    );
  }

  private cancelWaybill(wayBillId?: number): void {
    this.load(this.integrationsApiService.cancelBayanWaybill(this.shipment?.id, wayBillId), {
      isLoadingTransparent: true,
    }).subscribe(
      () => this.integrated.emit(),
      () => this.integrated.emit()
    );
  }
  onCloseCementDialog() {
    if (
      [
        ShipmentStatus.Assigned,
        ShipmentStatus.Accepted,
        ShipmentStatus.InTransit,
        ShipmentStatus.ArrivePickupLocation,
      ].includes(this.shipment.status)
    ) {
      this.integrated.emit();
    }
  }
}
